<script setup lang="ts">
import { AnalyticsClickBehaviour } from '@pkg/config/misc/analyticsClickBehaviour';
import { CmsDepth } from '@pkg/config/misc/cmsDepth';
import {
  NavigationMenuRoot,
  NavigationMenuList,
  NavigationMenuItem,
  NavigationMenuTrigger,
  NavigationMenuContent,
  NavigationMenuLink,
  NavigationMenuViewport,
} from 'radix-vue';
import { cn } from '@pkg/ui/lib/utils';
import PortableEditor from '@pkg/portable-text-editor/web/PortableEditor.vue';
import CustomLink from '@pkg/link/src/components/CustomLink.vue';
import { resolveComponent, inject, provide, ref, computed } from 'vue';
import HeaderActions from './HeaderActions.vue';
import NavDesktopAutocomplete from './NavDesktopAutocomplete.vue';
import NavSubmenuList from './NavSubmenuList.vue';
import MobileNav from './MobileNav.vue';
import { useDesktopHeader } from '../composables/useDesktopHeaderDropdowns';
import { resolveTopLevelMenuItems } from '../types/mainNav';
import type { MainNav } from '../types/mainNav';
import { locationAutocompleteKey, type LocationAutocompleteApi } from '../injectionKeys';

const props = defineProps<{
  menuContent: MainNav | null;
  /** Defaults to true when a location autocomplete API is available. */
  enableLocationSearch?: boolean;
  /** Prefer prop over inject — layout async setup can leave inject empty. */
  locationAutocomplete?: LocationAutocompleteApi | null;
}>();

const injectedAutocomplete = inject(locationAutocompleteKey, null);
const locationAutocomplete = props.locationAutocomplete ?? injectedAutocomplete;

// Re-provide so NavDesktopAutocomplete / MobileNav can keep using inject.
if (locationAutocomplete) {
  provide(locationAutocompleteKey, locationAutocomplete);
}

const showLocationSearch = computed(
  () => props.enableLocationSearch !== false && locationAutocomplete != null,
);

const TGTGLogo = resolveComponent('TGTGLogo');
const hasTgtgLogo = typeof TGTGLogo !== 'string';

const navRef = ref<HTMLElement | null>(null);
const viewportRef = ref<HTMLElement | null>(null);
const desktop = useDesktopHeader({ navRef, viewportRef });
const {
  mainNavOpenValue,
  autocompleteResultsOpen,
  autocompleteInputActive,
  languagePickerOpen,
  isMenuOpen,
  isSurfaceOpen,
  useOpenAppearance,
  surfaceStyle,
  barStyle,
  headerStyles,
  getMenuGridStyleFor,
  closeAll,
  closeNavMenu,
  setAutocompleteHasResults,
  mainNavInteractiveHovered,
  onNavMouseLeave,
} = desktop;

provide('headerThemeState', desktop.themeState);
provide('headerStyles', desktop.headerStyles);
provide('languagePickerOpen', desktop.languagePickerOpen);
provide('mainNavOpenValue', desktop.mainNavOpenValue);

const topLevelMenuItems = computed(() => resolveTopLevelMenuItems(props.menuContent));

const mobileMenuOpen = ref(false);

function setAutocompleteInputActive(v: boolean) {
  autocompleteInputActive.value = v;
}

const router = useRouter();
router.afterEach(() => {
  mobileMenuOpen.value = false;
  closeAll();
});

const { t } = useI18n();
</script>

<template>
  <header class="nav-header" :data-cms-depth="CmsDepth.Header">
    <a href="#main" class="skip-link">
      {{ t('skipToMainContent') }}
    </a>
    <nav
      ref="navRef"
      class="nav-header__desk w-full hidden desk:block"
      :style="useOpenAppearance ? headerStyles.transparentStyleWhenOpen : headerStyles.desktopNavStyle"
      aria-label="Main navigation"
      @mouseleave="onNavMouseLeave"
    >
      <NavigationMenuRoot
        v-model="mainNavOpenValue"
        :delay-duration="200"
        class="nav-desktop"
        :class="{
          'nav-desktop__bar--nav-open': !!mainNavOpenValue,
          'nav-desktop__bar--autocomplete-active': autocompleteInputActive || autocompleteResultsOpen,
          'nav-desktop__bar--language-picker-open': languagePickerOpen,
        }"
      >
        <div
          class="nav-desktop__surface"
          :class="{
            'nav-desktop__surface--open': isSurfaceOpen,
            'nav-desktop__surface--instant-fill': useOpenAppearance && !isSurfaceOpen,
          }"
          :style="surfaceStyle"
        >
          <div
            class="nav-desktop__bar"
            :style="barStyle"
          >
            <NavDesktopAutocomplete
              v-if="showLocationSearch"
              v-model:open="autocompleteResultsOpen"
              @input-active="setAutocompleteInputActive"
              @has-results="setAutocompleteHasResults"
              @close-nav-menu="closeNavMenu"
            />
            <NavigationMenuList
              class="nav__list"
              @mouseenter="mainNavInteractiveHovered = true"
              @mouseleave="mainNavInteractiveHovered = false"
            >
              <NavigationMenuItem
                v-for="(menu, i) in topLevelMenuItems"
                :key="i"
                :value="`menu-${i}`"
              >
                <NavigationMenuLink v-if="menu.directLink" as-child>
                  <CustomLink
                    :link="menu.directLink"
                    :title="menu.title"
                    :new-window="menu.newWindow"
                    class="nav__trigger nav__trigger--link"
                  >
                    {{ menu.title }}
                  </CustomLink>
                </NavigationMenuLink>
                <template v-else>
                  <!-- [html-validate-disable-next no-implicit-button-type, attribute-allowed-values] -->
                  <NavigationMenuTrigger class="nav__trigger">
                    {{ menu.title }}
                  </NavigationMenuTrigger>
                  <NavigationMenuContent class="nav__content absolute left-0 top-0 w-full">
                    <div
                      v-if="menu.submenuLists?.length || menu.additionalInfo?.length"
                      :class="cn('nav__grid nav-content-slide-down', headerStyles.navGridThemeClass)"
                      :style="getMenuGridStyleFor(menu)"
                    >
                      <div v-for="(list, j) in menu.submenuLists" :key="j" class="nav__col">
                        <span :id="`nav-col-${i}-${j}`" class="nav__col-title">
                          {{ list.submenuListTitle }}
                        </span>
                        <NavSubmenuList :list="list" />
                      </div>
                      <div
                        v-if="menu.additionalInfo?.length"
                        class="nav__col nav__col--additional"
                        :aria-label="`Additional content for ${menu.title}`"
                      >
                        <PortableEditor :content="menu.additionalInfo" class="nav__additional-info" />
                      </div>
                    </div>
                  </NavigationMenuContent>
                </template>
              </NavigationMenuItem>
            </NavigationMenuList>

            <div class="nav__logo amp-auto-track" :data-analytics-click-behaviour="AnalyticsClickBehaviour.HeaderLogo">
              <slot name="logo">
                <component
                  :is="TGTGLogo"
                  v-if="hasTgtgLogo"
                  header-logo
                  :theme="headerStyles.logoTheme"
                  fetchpriority="high"
                />
              </slot>
            </div>

            <div v-if="menuContent?.menuActions" class="nav__actions">
              <HeaderActions
                :nav-actions="menuContent?.menuActions"
                :mobile-header-action-key="menuContent?.mobileHeaderActionKey"
                :content-theme="headerStyles.logoTheme === 'dark' ? 'light' : 'dark'"
              />
            </div>
          </div>
          <div
            ref="viewportRef"
            class="c-nav__viewport"
            :class="{ 'c-nav__viewport--open': isSurfaceOpen }"
          >
            <div class="c-nav__viewport-body">
              <NavigationMenuViewport
                v-show="isMenuOpen"
                class="c-nav__viewport-inner c-nav__viewport-inner--nav"
                :data-state="isMenuOpen || autocompleteResultsOpen"
              />
              <div
                id="header-viewport-content"
                class="c-nav__viewport-inner c-nav__viewport-inner--teleport"
              />
            </div>
          </div>
        </div>
      </NavigationMenuRoot>
    </nav>

    <div class="desk:hidden">
      <MobileNav
        :is-open="mobileMenuOpen"
        :menu-content="props.menuContent"
        :open-menu="mobileMenuOpen"
        @toggle="mobileMenuOpen = !mobileMenuOpen"
        @close="mobileMenuOpen = false"
      />
    </div>
  </header>
</template>

<style>
:root {
  --header-height: 50px;
}

.nav-content-slide-down {
  animation: nav-content-reveal-diagonal 0.6s theme("transitionTiming.ease-out") forwards;
}

@keyframes nav-content-reveal-diagonal {
  from {
    opacity: 0;
    clip-path: inset(0 100% 100% 0);
  }
  to {
    opacity: 1;
    clip-path: inset(0 0 0 0);
  }
}

.nav__trigger--link {
  display: inline-flex;
  align-items: center;
  color: inherit;
  text-decoration: none;
}

.nav__trigger--link:hover,
.nav__trigger--link:focus-visible,
.nav__trigger--link.router-link-active {
  text-decoration: underline;
}

.nav__additional-info {
  font-size: theme('fontSize.14');
  max-width: 15rem;
}

.nav__grid--bg-light .nav__additional-info,
.nav-mobile__content--bg-light .nav__additional-info {
  color: theme('colors.neutral.70');
}
.nav__grid--bg-dark .nav__additional-info,
.nav-mobile__content--bg-dark .nav__additional-info {
  color: theme('colors.neutral.50');
}
</style>

<style scoped>
.skip-link {
  position: absolute;
  left: -9999px;
  z-index: 9999;
  padding: theme('spacing.8') theme('spacing.16');
  color: var(--theme-header-text-color);
  background: var(--theme-header-background-color);
  font-weight: theme('fontWeight.medium');
  &:focus-visible {
    left: theme('spacing.16');
    top: theme('spacing.16');
  }

  &:focus-visible,
  :deep(.nav__trigger:focus-visible) {
    outline: none;
    box-shadow: 0 0 0 2px currentColor;
  }
}

.nav-header {
  position: fixed;
  inset: 0 0 auto 0;
  z-index: 5;
  font-size: theme('fontSize.14');
}

/* Language picker open: header above Radix portal so teleported list receives clicks */
.nav-header:has(.nav-desktop__bar--language-picker-open) {
  z-index: 10000;
}

/* Open/close height follows content; Radix sets --radix-navigation-menu-viewport-* on the viewport node */
.c-nav__viewport {
  position: relative;
  display: grid;
  grid-template-rows: 0fr;
  overflow: hidden;
  transition: grid-template-rows theme("transitionDuration.snail") theme("transitionTiming.ease-out");
}

.c-nav__viewport--open {
  grid-template-rows: 1fr;
}

.c-nav__viewport-body {
  display: flex;
  min-height: 0;
  flex-direction: column;
  overflow: hidden;
}

.c-nav__viewport-inner {
  color: inherit;
  transition: opacity theme("transitionDuration.snail") theme("transitionTiming.ease-out");
}

.c-nav__viewport-inner--nav {
  position: relative;
  width: var(--radix-navigation-menu-viewport-width, 100%);
  height: var(--radix-navigation-menu-viewport-height, 0px);
  overflow: hidden;
}

.c-nav__viewport-inner--teleport {
  display: flex;
  flex: 0 0 auto;
  flex-direction: column;
  min-height: 0;
}

.c-nav__viewport--open .c-nav__viewport-inner {
  background: transparent;
  backdrop-filter: none;
  -webkit-backdrop-filter: none;
}

.nav-desktop {
  display: flex;
  flex-direction: column;

  &__surface {
    position: relative;
    display: flex;
    flex-direction: column;
    width: 100%;
    transition:
      background-color theme("transitionDuration.goat") theme("transitionTiming.ease-out"),
      backdrop-filter theme("transitionDuration.goat") theme("transitionTiming.ease-out"),
      -webkit-backdrop-filter theme("transitionDuration.goat") theme("transitionTiming.ease-out");

    &--instant-fill {
      transition: none;
    }
  }

  &__bar {
    position: relative;
    z-index: 10;
    display: flex;
    width: 100%;
    align-items: center;
    justify-content: space-between;
    padding: theme('spacing.12') theme('spacing.40') theme('spacing.12') theme('spacing.32');

    &:deep(ul.nav__list) {
      flex: 1;
      display: flex;
      flex-direction: row;
      flex-wrap: nowrap;
      list-style: none;
      margin: 0;
      padding: 0;
      align-items: center;
      justify-content: flex-start;
    }
    &:deep(ul.nav__list > li) {
      display: flex;
      flex-shrink: 0;
      margin-right: theme('spacing.32');
    }
    /* Dim non-active items when nav, autocomplete, or language picker is in use */
    :deep(.nav__list:has(.nav__trigger:hover)) .nav__trigger:not(:hover),
    :deep(.nav__list:has(.nav__trigger[data-state='open'])) .nav__trigger:not([data-state='open']),
    :deep(&--nav-open .nav__list .nav__trigger:not([data-state='open'])),
    :deep(&--autocomplete-active .nav__trigger),
    :deep(&--language-picker-open .nav__trigger) {
      opacity: 0.6;
    }
    &:has(.nav__list .nav__trigger:hover) :deep(.nav-desktop-autocomplete__wrap),
    &:has(.nav__list .nav__trigger[data-state='open']) :deep(.nav-desktop-autocomplete__wrap),
    :deep(&--nav-open .nav-desktop-autocomplete__wrap),
    :deep(&--language-picker-open .nav-desktop-autocomplete__wrap) {
      opacity: 0.6;
    }
    &:has(.nav__list .nav__trigger:hover) :deep(.nav__actions .c-nav__links-item),
    &:has(.nav__list .nav__trigger[data-state='open']) :deep(.nav__actions .c-nav__links-item),
    :deep(&--nav-open .nav__actions .c-nav__links-item),
    :deep(&--autocomplete-active .nav__actions .c-nav__links-item),
    :deep(&--language-picker-open .nav__actions .c-nav__links-item) {
      opacity: 0.6;
    }
  }
}

.nav {
  &__grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    align-content: start;
    gap: theme('spacing.16') theme('spacing.24');
    padding: theme('spacing.24') theme('spacing.40');
    &--bg-light {
      --nav-col-separator: 1px solid color-mix(in srgb, theme('colors.black') 25%, transparent);
    }
    &--bg-dark {
      --nav-col-separator: 1px solid color-mix(in srgb, theme('colors.white') 25%, transparent);
    }
  }

  &__col {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: theme('spacing.8');
    min-width: 0;

    &:not(:nth-child(4n + 1)) {
      border-left: var(--nav-col-separator);
      padding-left: theme('spacing.24');
    }
    &-title {
      font-size: theme('fontSize.14');
      font-weight: theme('fontWeight.bold');
      color: inherit;
    }
  }

  &__logo {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    width: 5rem;
    height: 4rem;
    padding-left: 1rem;
    z-index: 10;
  }

  &__actions {
    flex: 1;
    display: flex;
    justify-content: flex-end;
  }
}
</style>
